import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { oapi } from '@/lib/officialsApi';

const PriceCard = ({ cfg, onSaved }) => {
  const [price, setPrice] = useState(String(cfg.price));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onSaved(await oapi('/admin/officials-addon/config', { method: 'PUT', body: { price: Number(price) } })); toast.success('Add-on price updated'); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Yearly price</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-center">
          <span className="text-slate-500 font-semibold">$</span>
          <Input type="number" min={1} step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="w-32" data-testid="admin-addon-price-input" />
          <Button onClick={save} disabled={busy} className="bg-[#00247D]" data-testid="admin-addon-price-save">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save</Button>
        </div>
        <p className="text-xs text-slate-500">New subscribers pay the new price. Existing PayPal subscriptions keep the price they signed up at.</p>
        <p className="text-sm text-slate-700" data-testid="admin-addon-active-count"><b>{cfg.active_subscribers}</b> accounts currently have access</p>
      </CardContent>
    </Card>
  );
};

const GrantCard = ({ onChanged }) => {
  const [email, setEmail] = useState('');
  const [days, setDays] = useState('365');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const grant = async (active) => {
    setBusy(true);
    try {
      const r = await oapi('/admin/officials-addon/grant', { method: 'POST', body: { email, days: Number(days) || 365, active } });
      setLast(r);
      toast.success(active ? `Access given to ${r.email}` : `Access removed for ${r.email}`);
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Give or remove free access</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-2">
          <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Account email</Label><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="league@example.com" data-testid="admin-addon-grant-email" /></div>
          <div className="space-y-1"><Label className="text-xs">Days</Label><Input type="number" min={1} value={days} onChange={e => setDays(e.target.value)} data-testid="admin-addon-grant-days" /></div>
        </div>
        <div className="flex gap-2">
          <Button disabled={busy || !email.trim()} onClick={() => grant(true)} className="bg-[#009E49] hover:bg-[#008840]" data-testid="admin-addon-grant-btn">Give access</Button>
          <Button variant="outline" disabled={busy || !email.trim()} onClick={() => grant(false)} className="text-red-600" data-testid="admin-addon-revoke-btn">Remove access</Button>
        </div>
        {last && <p className="text-xs text-slate-600" data-testid="admin-addon-grant-result">{last.email}: {last.addon.active ? `active until ${new Date(last.addon.expires_at).toLocaleDateString()}` : 'no access'}</p>}
      </CardContent>
    </Card>
  );
};

export const OfficialsAddonAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => { oapi('/admin/officials-addon/config').then(setCfg).catch(e => toast.error(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="admin-officials-tab">
      <div className="flex items-center gap-2 text-[#00247D]"><ShieldCheck className="w-5 h-5" /><h2 className="text-xl font-bold">Officials Scheduling add-on</h2></div>
      {!cfg ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          <PriceCard cfg={cfg} onSaved={c => setCfg(p => ({ ...p, ...c }))} />
          <GrantCard onChanged={load} />
        </div>
      )}
    </div>
  );
};
